import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { Skin } from '@sorare/core/src/__generated__/globalTypes';
import { Cards } from '@sorare/core/src/assets/icons/Cards.tsx';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BrandXS,
  LabelL,
  LabelS,
} from '@sorare/core/src/atoms/typography/index.tsx';
import { HorizontalDivider } from '@sorare/core/src/atoms/ui/Divider/index.tsx';
import { SkinIcon } from '@sorare/core/src/components/collections/SkinIcon';
import { groupBy } from '@sorare/core/src/lib/arrays.ts';
import { formatSearchString } from '@sorare/core/src/lib/string/formatSearchString';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { CollectionGradientWrapper } from '#src/components/collections/CollectionPreview/CollectionGradientWrapper/index.tsx';
import { CollectionPreviewHeader } from '#src/components/collections/CollectionPreview/CollectionPreviewHeader/index.tsx';
import { EmptyCollection } from '#src/components/collections/CollectionPreview/EmptyCollection/index.tsx';
import { Shimmer } from '#src/components/collections/CollectionPreview/Shimmer/index.tsx';
import { useCardCollectionData } from '#src/components/collections/CollectionPreview/useCardCollectionData.ts';
import { CollectionSlot } from '#src/components/myCards/LeagueCollectionPreview/CollectionSlot/index.tsx';
import { useSetHasMultipleSkins } from '#src/hooks/my-cards/useSetHasMultipleSkins.ts';
import type { CommonCollectionPreview_cardCollection } from './__generated__/index.graphql.ts';

export type { CommonCollectionPreview_cardCollection };

const Root = styled(Vertical)`
  border-radius: var(--double-unit);
  height: 100%;
  background: var(--c-black);
  overflow: hidden;
`;

const SlotsWrapper = styled(Vertical)`
  padding: var(--double-unit);
`;

const SlotsGrid = styled.div`
  display: grid;
  gap: var(--half-unit);
  grid-template-columns: repeat(5, minmax(0, 1fr));

  @media ${laptopAndAbove} {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

type Props = {
  collection: CommonCollectionPreview_cardCollection;
  setSlug: string;
  to?: string;
  playerNameFilter?: string;
  showOwnedOnly?: boolean;
  skin: Nullable<Skin>;
};

export const CommonCollectionPreview = ({
  collection,
  to,
  playerNameFilter,
  showOwnedOnly,
  setSlug,
  skin,
}: Props) => {
  const { userCardCollection, rarity, team } = collection;
  const { complete } = userCardCollection || {};
  const { hexColor } = useCardCollectionData(collection);
  const displaySkinHeader = useSetHasMultipleSkins(setSlug);

  if (!team) {
    return null;
  }

  const filtersEnabled = showOwnedOnly || !!playerNameFilter || skin;

  const { fulfilledSlotsCount = 0 } = userCardCollection || {};
  if (!fulfilledSlotsCount) {
    if (filtersEnabled) {
      return null;
    }
    return <EmptyCollection cardCollection={collection} to={to} />;
  }

  const normalizedFilter = formatSearchString(playerNameFilter);
  const slotsToDisplay =
    userCardCollection?.slots.filter(slot => {
      if (showOwnedOnly && !slot.cardCollectionCardsCount) {
        return false;
      }
      if (!normalizedFilter) {
        return true;
      }
      return formatSearchString(slot.slot?.anyPlayer?.displayName)?.includes(
        normalizedFilter
      );
    }) || [];

  if (slotsToDisplay.length === 0) {
    return null;
  }

  const groupedAllSlots = groupBy(
    userCardCollection?.slots,
    slot => slot.skinEdition?.skin ?? Skin.STANDARD
  );

  const groupedSlots = groupBy(
    slotsToDisplay,
    slot => slot.skinEdition?.skin ?? Skin.STANDARD
  );

  const displaySkinStats = !playerNameFilter;

  return (
    <CollectionGradientWrapper
      className={classNames({ complete })}
      style={
        {
          '--completed-color': hexColor,
        } as CSSProperties
      }
    >
      <Root>
        <CollectionPreviewHeader collection={collection} to={to} />
        <SlotsWrapper gap={2}>
          {Object.entries(groupedSlots).map(([skinKey, slots]) => (
            <Vertical key={skinKey} gap={1}>
              {!!displaySkinHeader && (
                <>
                  <SBHorizontal>
                    <Horizontal gap={0.5}>
                      <SkinIcon skin={skinKey as Skin} size={3} />
                      <BrandXS>{slots.at(0)?.skinEdition?.skinName}</BrandXS>
                    </Horizontal>
                    {!!displaySkinStats && (
                      <Horizontal gap={0.5}>
                        <span>
                          <LabelL bold as="span">
                            {groupedAllSlots[skinKey as Skin]?.filter(
                              slot => slot.cardCollectionCardsCount
                            ).length || 0}
                          </LabelL>
                          <LabelS as="span" color="var(--c-nd-700)">
                            /{groupedAllSlots[skinKey as Skin]?.length || 1}
                          </LabelS>
                        </span>
                        <Cards size={1.5} />
                      </Horizontal>
                    )}
                  </SBHorizontal>
                  <HorizontalDivider />
                </>
              )}
              <SlotsGrid>
                {slots.map(slot => (
                  <CollectionSlot
                    key={slot.slug}
                    userCardCollectionSlot={slot}
                    rarity={rarity}
                    displayPlayerName={!!playerNameFilter}
                  />
                ))}
              </SlotsGrid>
            </Vertical>
          ))}
        </SlotsWrapper>
        {!!complete && <Shimmer rarity={rarity} />}
      </Root>
    </CollectionGradientWrapper>
  );
};

CommonCollectionPreview.fragments = {
  cardCollection: gql`
    fragment CommonCollectionPreview_cardCollection on CardCollection {
      slug
      rarity
      team: anyTeam {
        slug
        name
        shortName
        pictureUrl
        styleGuideColor
      }
      userCardCollection(forUserSlug: $user) {
        id
        fulfilledSlotsCount
        complete
        slots(skin: $skin) {
          slug
          skinEdition {
            skin
            skinName
          }
          slot {
            id
            anyPlayer {
              slug
              displayName
            }
          }
          ...CollectionSlot_userCardCollectionSlot
        }
      }
      ...CollectionPreviewHeader_cardCollection
      ...useCardCollectionData_cardCollection
      ...EmptyCollection_cardCollection
    }
    ${CollectionPreviewHeader.fragments.cardCollection}
    ${CollectionSlot.fragments.userCardCollectionSlot}
    ${useCardCollectionData.fragments.cardCollection}
    ${EmptyCollection.fragments.cardCollection}
  ` as TypedDocumentNode<CommonCollectionPreview_cardCollection>,
};
